import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { dateformatwithuktime } from "../utilityfunctions";
import Popup from "../common/popup";

export const SelectCampaignEmails = ({
  setSelectedAccounts,
  setAllAccounts,
  filterRefresh,
  setFilterRefresh,
  setAccountFilter,
  setCustomHoursFilter,
  setEmailCampaignName,
  emailTypeOptions,
  campaignNameError,
  setCampaignNameError,
  errors,
  setErrors,
  emailType,
  setEmailType,
  emailData,
  setEmailData,
  setAllAccountsDisabled,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [filter, setFilter] = useState("");
  const [topOrderedCustomers, setTopOrderedCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [selectedAccountsInternal, setSelectedAccountsInternal] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [restrictedAccounts, setRestrictedAccounts] = useState([]);
  const [restrictedLoaded, setRestrictedLoaded] = useState(false);
  const [customHours, setCustomHours] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [pendingFilter, setPendingFilter] = useState("");
  const [pendingCustomHours, setPendingCustomHours] = useState("");
  const [customHoursError, setCustomHoursError] = useState("");
  const [showQueuedPopup, setShowQueuedPopup] = useState(false);
  const [queuedPopupData, setQueuedPopupData] = useState(null);
  const [queuedLoading, setQueuedLoading] = useState(false);
  const [selectedAccountCode, setSelectedAccountCode] = useState("");
  // const [emailType, setEmailType] = useState("logged_in_not_order");
  const [emailCampaignNameLocal, setEmailCampaignNameLocal] = useState("");
  const getFilterLabel = () => {
    if (filter === 24) return t("hrs_ago_24");
    if (filter === 48) return t("hrs_ago_48");
    if (filter === 96) return t("hrs_ago_96");
    if (filter === "custom") return t("hrs_ago_custom", { hours: customHours });
    if (filter === "") return "";
    return "";
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsFilterOpen(false);
        setPendingFilter(filter);
        setPendingCustomHours(customHours);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filter, customHours]);

  useEffect(() => {
    setSelectedAccountsInternal([]);
    setSelectedAccounts([]);
    setSelectAll(false);
  }, [topOrderedCustomers, setSelectedAccounts]);

  const handleToggleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedAccountsInternal([]);
      setSelectedAccounts([]);
      setSelectAll(false);
    } else {
      // Select only enabled accounts (not alreadyQueued)
      const enabledAccounts = topOrderedCustomers.filter(
        (acc) => !acc.alreadyQueued
      );
      setSelectedAccountsInternal(enabledAccounts);
      setSelectedAccounts(enabledAccounts);
      setSelectAll(true);
    }
  };

  const handleToggleAccount = (account) => {
    setSelectedAccountsInternal((prev) => {
      let updated;
      if (prev.find((acc) => acc.accountCode === account.accountCode)) {
        updated = prev.filter((acc) => acc.accountCode !== account.accountCode);
      } else {
        updated = [...prev, account];
      }
      setSelectAll(updated.length === topOrderedCustomers.length);
      setSelectedAccounts(updated);
      return updated;
    });
  };

  useEffect(() => {
    http
      .get(BaseUrl + "admin/accountcodelist")
      .then((res) => {
        const ids = res.data?.data?.map((acc) => acc.accountCode);
        setRestrictedAccounts(ids || []);
      })
      .catch((err) => {
        console.error("Failed to fetch restricted accounts", err);
        setRestrictedAccounts([]);
      })
      .finally(() => {
        setRestrictedLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (emailType === "") {
      setTopOrderedCustomers([]);
      return;
    }
    if (!restrictedLoaded) {
      return;
    }
    // Do not call API until a cutoff filter is applied (filter is non-empty)
    if (
      filter === "" ||
      filter === null ||
      typeof filter === "undefined" ||
      (filter === "custom" && !customHours)
    ) {
      setTopOrderedCustomers([]);
      setIsLoading(false);
      return;
    }
    // setIsLoading(true);
    let payload = {};
    if (filter === "custom" && customHours) {
      payload = {
        filterBy: parseInt(customHours),
      };
    } else {
      payload = {
        filterBy: parseInt(filter),
      };
    }
    // Dynamically decide which API to call based on selected email type
    let URL2 = "";
    if (emailType === "logged_in_not_order") {
      URL2 = BaseUrl + "admin/getLoggedInNotOrdered";
    } else if (emailType === "logged_in_not_order_cutoff") {
      URL2 = BaseUrl + "admin/getLoggedInButNotOrderedCutOff";
    } else if (emailType === "abandoned_cart") {
      URL2 = BaseUrl + "admin/getAbandonedCart";
    }
    http
      .post(URL2, payload)
      .then((response) => {
        setIsLoading(false);
        const filteredData = response.data.data.filter(
          (item) => !restrictedAccounts.includes(item.accountCode)
        );
        setTopOrderedCustomers(filteredData);
        setIsLoading(false);
        setIsButtonLoading(false);
        setAllAccounts(filteredData);
      })
      .catch((err) => {
        setIsLoading(false);
        setIsButtonLoading(false);
        console.log(err);
      });
  }, [filter, restrictedLoaded, customHours, filterRefresh]);

  // checks if all accounts are disabled
  useEffect(() => {
    if (!setAllAccountsDisabled) return;

    if (topOrderedCustomers.length === 0) {
      setAllAccountsDisabled(false);
      return;
    }
    const allDisabled = topOrderedCustomers.every(
      (acc) => acc.alreadyQueued === true
    );
    setAllAccountsDisabled(allDisabled);
  }, [topOrderedCustomers, setAllAccountsDisabled]);

  // Reset cutoff selection when email type changes
  useEffect(() => {
    // when email type changes, user must pick cutoff again
    setPendingFilter("");
    setPendingCustomHours("");
    setFilter("");
    setCustomHours("");
    // also reset selected accounts and selectAll so user re-selects if needed
    setSelectedAccountsInternal([]);
    setSelectedAccounts([]);
    setSelectAll(false);
    setTopOrderedCustomers([]);
  }, [emailType, setSelectedAccounts]);

  useEffect(() => {
    if (!topOrderedCustomers.length) {
      setSelectedAccounts([]);
      return;
    }
    const selectedForParent = selectedAccountsInternal.map((a) => a);
    setSelectedAccounts(selectedForParent);
  }, [selectedAccountsInternal, topOrderedCustomers]);

  const isAutoEmailDisabled =
    !emailType ||
    pendingFilter === "" ||
    topOrderedCustomers.length === 0 ||
    (pendingFilter === "custom" &&
      (!pendingCustomHours ||
        String(pendingCustomHours).trim() === "" ||
        isNaN(pendingCustomHours) ||
        Number(pendingCustomHours) <= 0));

  // Auto select/deselect all when autoEmail is toggled
  useEffect(() => {
    if (!topOrderedCustomers.length) return;
    if (emailData?.autoEmail) {
      // Auto Email is ON select all non-disabled accounts
      const enabledAccounts = topOrderedCustomers.filter(
        (acc) => !acc.alreadyQueued
      );
      setSelectedAccountsInternal(enabledAccounts);
      setSelectedAccounts(enabledAccounts);
      setSelectAll(true);
    } else {
      // Auto Email is OFF deselect all
      setSelectedAccountsInternal([]);
      setSelectedAccounts([]);
      setSelectAll(false);
    }
  }, [emailData?.autoEmail, topOrderedCustomers]);

  const handleQueuedClick = async (accountCode) => {
    setSelectedAccountCode(accountCode);
    setQueuedLoading(true);
    setShowQueuedPopup(true);
    try {
      const res = await http.post(
        BaseUrl + "admin/getEmailCampaignLogsbyAccountCode",
        { accountCode }
      );
      const rawData = res.data?.data || [];
      const formatted = rawData.map((item) => ({
        campaignName: item.campaignName || "-",
        emailType: item.emailType || "-",
        autoEmail: item.autoEmail ?? "-",
        runStatus: item.runStatus || "-",
        customerRunStatus: item.lastCustomerRunStatus || "-",
        emailSentStatus: item.lastCustomerSentStatus || "-",
        scheduledSendTime: item.lastScheduledSendTime || null,
        emailSentDate: item.lastCustomerSentDate || null,
        emailSentCount: item.totalSent ?? "-",
      }));
      const sortedData = formatted.sort((a, b) => {
        const order = { run: 1, pause: 2, stop: 3 };
        return (order[a.runStatus] || 999) - (order[b.runStatus] || 999);
      });
      setQueuedPopupData(sortedData);
    } catch (err) {
      console.error(err);
      setQueuedPopupData([]);
    }
    setQueuedLoading(false);
  };
  // Close and reset popup when main filters or email type changes
  useEffect(() => {
    setShowQueuedPopup(false);
    setQueuedPopupData(null);
  }, [emailType, filterRefresh]);
  // Helper fot date format
  const formatSafe = (date) => {
    if (!date) return "-";
    const formatted = dateformatwithuktime(date);
    return formatted === "Invalid date" ? "-" : formatted;
  };
  const queuedColumns = [
    {
      name: t("campaign_namee_txt"),
      selector: (row) => row.campaignName || "-",
      sortable: true,
    },
    {
      name: t("email_type_txt"),
      selector: (row) =>
        row.emailType === "logged_in_not_order"
          ? t("logged_in_ordered_email_txt")
          : row.emailType === "logged_in_not_order_cutoff"
          ? t("logged_in_ordered_email_cutoff_txt")
          : row.emailType === "abandoned_cart"
          ? t("abandoned_cart_email_txt")
          : row.emailType,
      sortable: true,
    },
    {
      name: `${t("campaign_mode_txt")} / ${t("status_txt")}`,
      selector: (row) => {
        const mode = row.autoEmail ? "Auto" : "Manual";
        const status =
          row.runStatus === "run"
            ? t("running_txt")
            : row.runStatus === "pause"
            ? t("paused_txt")
            : row.runStatus === "stop"
            ? t("completed_txt")
            : row.runStatus || "-";

        return `${mode} / ${status}`;
      },
      sortable: true,
    },
    {
      name: t("customer_runstatus_txt"),
      selector: (row) => {
        const cusrunstatus = row.customerRunStatus;
        if (!cusrunstatus) return "-";
        return cusrunstatus === "run"
          ? t("running_txt")
          : cusrunstatus === "pause"
          ? t("paused_txt")
          : cusrunstatus === "stop"
          ? t("completed_txt")
          : cusrunstatus;
      },
      sortable: true,
    },
    // {
    //   name: t("reminder_limit_txt"),
    //   selector: (row) => row.reminderCountLimit ?? "-",
    //   sortable: true,
    // },
    {
      name: t("reminder_count_txt"),
      selector: (row) => row.emailSentCount  && row.emailSentCount > 0 ? row.emailSentCount : "-",
      sortable: true,
    },
    {
      name: t("last_email_sent_date"),
      selector: (row) =>
        row.emailSentCount > 0 && row.emailSentDate ? formatSafe(row.emailSentDate) : "-",
    },
    {
      name: t("email_status_txt"),
      selector: (row) =>
        row.emailSentStatus
          ? row.emailSentStatus.charAt(0).toUpperCase() +
            row.emailSentStatus.slice(1)
          : "-",
    },
    {
      name: t("scheduled_date_txt"),
      selector: (row) =>
        row.emailSentStatus?.toLowerCase() === "pending" &&
        row.scheduledSendTime
          ? formatSafe(row.scheduledSendTime)
          : "-",
    },
  ];

  const columns = [
    {
      name: (
        <div className="full-text-toggle custom_check-box">
          <div className="check-box no-strike">
            <input
              type="checkbox"
              id="selectAllHeader"
              checked={selectAll}
              onChange={handleToggleSelectAll}
            />
            <label htmlFor="selectAllHeader"></label>
          </div>
        </div>
      ),
      cell: (row) => (
        <div className="full-text-toggle custom_check-box">
          <div className="check-box no-strike">
            <input
              type="checkbox"
              id={`fullTextToggle-${row.accountCode}`}
              checked={
                row.alreadyQueued ||
                selectedAccountsInternal.some(
                  (acc) => acc.accountCode === row.accountCode
                )
              }
              disabled={row.alreadyQueued}
              onChange={() => handleToggleAccount(row)}
            />
            <label htmlFor={`fullTextToggle-${row.accountCode}`}></label>
          </div>
        </div>
      ),
    },
    {
      name: t("account_code_txt"),
      selector: (row) => row.accountCode,
      sortable: true,
    },
    {
      name: t("account_name_txt"),
      selector: (row) => row.accountName,
      sortable: true,
    },
    {
      name: t("email_address_txt"),
      selector: (row) => row.accountEmail,
    },
    {
      name: t("last_login_txt"),
      selector: (row) => moment(row.lastLogin).format("DD/MM/YYYY"),
      sortable: false,
    },
    {
      name: t("already_queued_email_txt"),
      selector: (row) => {
        const hasSentEmails = row.totalEmailSentCount > 0 || row.alreadyQueued!=false ;
        const statusText = hasSentEmails
          ? t("queued_email_details_txt")
          : "";
        const handleClick = (e) => {
          if (!hasSentEmails) return;
          e.preventDefault();
          handleQueuedClick(row.accountCode);
        };
        return (
          <a
            href="#"
            className={`title-tooltip title-up ${
              hasSentEmails ? "button" : ""
            }`}
            data-title={hasSentEmails ? statusText : ""}
            onClick={handleClick}
            style={{
              cursor: hasSentEmails ? "pointer" : "default",
              pointerEvents: hasSentEmails ? "auto" : "none",
            }}
          >
            {row.alreadyQueued ? (
              <span className="price-ord-yes">
                <i className="fa fa-check" aria-hidden="true"></i>
              </span>
            ) : (
              <span className="price-ord-no">
                <i className="fa fa-times" aria-hidden="true"></i>
              </span>
            )}
          </a>
        );
      },
    },
    // {
    //     name:"Last Sent At",
    //     selector: row => row.lastEmailSentDate ? (
    //         <a class="title-tooltip title-up"  data-title={`Reminder # ${row.lastReminderCount || 0}`}>{formatDate(row.lastEmailSentDate)}</a>

    //     ) : " "
    // }
    {
      name: t("email_last_sent_at_txt"),
      selector: (row) =>
        row.lastEmailSentDate
          ? dateformatwithuktime(row.lastEmailSentDate)
          : "",
    },
    {
      name: t("reminder_count_txt"),
      selector: (row) =>
        row.totalEmailSentCount !== undefined && row.totalEmailSentCount > 0
          ? row.totalEmailSentCount
          : "",
      sortable: true,
    },
  ];

  // Disable Refresh when no emailType selected OR no pendingFilter chosen OR (custom selected but invalid)
  const isCustomInvalid =
    pendingFilter === "custom" &&
    (!pendingCustomHours ||
      String(pendingCustomHours).trim() === "" ||
      isNaN(pendingCustomHours) ||
      Number(pendingCustomHours) <= 0);
  const isRefreshDisabled =
    !emailType || pendingFilter === "" || isCustomInvalid;

  const customStyles = {
    rows: {
      style: {
        minHeight: "25px",
      },
    },
    headCells: {
      style: {
        padding: "8px",
      },
    },
    cells: {
      style: {
        padding: "8px",
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: "",
    rangeSeparatorText: "",
    selectAllRowsItem: true,
    selectAllRowsItemText: "",
    noRowsPerPage: "",
  };

  let loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div className={`chart-container logged-in-not-ordered-email-customers`}>
      <div className="flex-bx-wrap col2">
        <div className="flex-bx-item">
          <div className="input-field">
            <label>{t("campaign_name_txt")}<span className="color-red">*</span></label>
            <input
              type="text"
              value={emailCampaignNameLocal}
              onChange={(e) => {
                const val = e.target.value;
                setEmailCampaignNameLocal(val);
                if (setEmailCampaignName) setEmailCampaignName(val);
                if (setCampaignNameError) setCampaignNameError("");
              }}
              placeholder={t("enter_campaign_name_txt")}
            />
            {campaignNameError && (
              <div className="error-msg">{campaignNameError}</div>
            )}
          </div>
        </div>
        <div className="flex-bx-item">
          <div className="input-field">
            <label>{t("email_type_txt")}<span className="color-red">*</span></label>
            <select
              value={emailType}
              name="emailType"
              onChange={(e) => {
                const selectedType = e.target.value;
                setEmailType(selectedType);
                //setFilterRefresh((prev) => prev + 1);
                setErrors({});
                // setErrors((prev) => ({
                //   ...prev,
                //   emailType: "",
                // }));
              }}
            >
              <option value="">{t("select_txt")}</option>
              {emailTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.name}
                </option>
              ))}
              {/* <option value="logged_in_not_order">
                {t("logged_in_ordered_email_txt")}
              </option>
              <option value="abandoned_cart">
                {t("abandoned_cart_email_txt")}
              </option> */}
            </select>
            {errors.emailType && (
              <div className="error-msg">{errors.emailType}</div>
            )}
          </div>
        </div>
      </div>
      <div className="flex-bx-wrap col2">
        <div className="flex-bx-item">
          <div className="input-field">
            <label>
              {t("cutoff_time_txt")}
              {emailType && (
                <>
                  {" - "}
                  {emailType === "logged_in_not_order" &&
                    t("logged_in_ordered_email_txt")}
                  {emailType === "logged_in_not_order_cutoff" &&
                    t("logged_in_ordered_email_cutoff_txt")}
                  {emailType === "abandoned_cart" &&
                    t("abandoned_cart_email_txt")}
                </>
              )}
              <span className="color-red">*</span>
            </label>
            {emailType && pendingFilter !== "" && (
              <a
                className="title-tooltip title-right switch-info"
                data-title={t("click_fetch_btn_info")}
              >
                <i className="fa fa-info-circle" aria-hidden="true"></i>
              </a>
            )}
            <select
              value={pendingFilter}
              onChange={(e) => {
                setIsLoading(true);
                setTopOrderedCustomers([]);
                setSelectedAccounts([]);
                setCustomHoursError("");
                const val =
                  e.target.value === ""
                    ? ""
                    : e.target.value === "custom"
                    ? "custom"
                    : parseInt(e.target.value, 10);
                setPendingFilter(val);
                if (val !== "custom" && val != "") {
                  setPendingCustomHours("");
                  setAccountFilter(val);
                  setCustomHoursFilter("");
                } else if (val === "") {
                  setAccountFilter("");
                  setCustomHoursFilter("");
                  setTopOrderedCustomers([]);
                  setFilter("");
                  setCustomHours("");
                }
              }}
              disabled={!emailType}
            >
              <option value="">{t("select_txt")}</option>
              <option value={24}> {t("hour_txt", { hoursAgo: 24 })}</option>
              <option value={48}> {t("hour_txt", { hoursAgo: 48 })}</option>
              <option value={96}> {t("hour_txt", { hoursAgo: 96 })}</option>
              <option value="custom">{t("custom_txt")}</option>
            </select>
          </div>
        </div>
        {pendingFilter === "custom" && (
          <div className="flex-bx-item">
            <div className="input-field">
              <label>{t("custom_hr_txt")}<span className="color-red">*</span></label>
              <input
                type="number"
                value={pendingCustomHours}
                placeholder={t("custom_hrs_text")}
                min="1"
                onKeyDown={(e) => {
                  if (["-", "+", ".", "e", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  setCustomHoursError("");
                  setIsLoading(true);
                  setSelectedAccounts([]);
                  setTopOrderedCustomers([]);
                  const value = e.target.value;
                  if (
                    value === "" ||
                    (/^[0-9\b]+$/.test(value) && Number(value) > 0)
                  ) {
                    setPendingCustomHours(value);
                    setCustomHoursFilter(value);
                    setAccountFilter("custom");
                  }
                }}
                disabled={!emailType}
              />
              {customHoursError && (
                <div className="error-msg">{customHoursError}</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex-bx-wrap">
        <div className="flex-bx-item">
          <div className="input-field fetch-btn-wrap">
            <button
              type="button"
              className={`button refresh-btn ${
                isRefreshDisabled ? "disabled" : ""
              } ${isButtonLoading ? "loader-btn" : ""}`}
              onClick={() => {
                setIsButtonLoading(true); 
                setCustomHoursError("");
                setIsLoading(true);
                setTopOrderedCustomers([]);
                // If nothing selected (Select option)
                if (pendingFilter === "" || pendingFilter === null) {
                  console.log("⏸ No cutoff selected -> clear old data");
                  setTopOrderedCustomers([]); // clear table
                  setFilter(""); // reset filter state
                  setCustomHours(""); // reset custom hours
                  setCustomHoursFilter("");
                  setAccountFilter("");
                  setIsButtonLoading(false);
                  return; // stop further logic
                }
                if (pendingFilter === "custom") {
                  if (
                    !pendingCustomHours ||
                    String(pendingCustomHours).trim() === "" ||
                    isNaN(pendingCustomHours) ||
                    Number(pendingCustomHours) <= 0
                  ) {
                    setCustomHoursError(t("please_enter_hour_txt"));
                    setIsButtonLoading(false);
                    return;
                  }
                  setCustomHours(pendingCustomHours);
                  if (setCustomHoursFilter)
                    setCustomHoursFilter(pendingCustomHours);
                } else {
                  setCustomHours("");
                  if (setCustomHoursFilter) setCustomHoursFilter("");
                }
                setFilter(pendingFilter);
                setFilterRefresh((prev) => prev + 1);
              }}
              disabled={isRefreshDisabled}
            >
              {t("refresh_txt")}
            </button>
          </div>
        </div>
      </div>
      <div className="flex-bx-wrap">
        <div className="flex-bx-item col-1">
          <label htmlFor="autoEmail">{t("auto_email_txt")}</label>
          <div className="switch-button">
            <input
              type="checkbox"
              id="autoEmail"
              name="autoEmail"
              className="checkbox"
              checked={emailData?.autoEmail}
              disabled={isAutoEmailDisabled}
              onChange={(e) => {
                setEmailData((prev) => ({
                  ...prev,
                  autoEmail: e.target.checked,
                }));
                // setErrors((prev) => ({
                //   ...prev,
                //   autoEmail: "",
                // }));
              }}
            />
            <div className="knobs">
              <span></span>
            </div>
            <div className="layer"></div>
          </div>
          <a
            className="title-tooltip title-right switch-info"
            data-title={t("switch_info_text")}
          >
            <i className="fa fa-info-circle" aria-hidden="true"></i>
          </a>
          {/* <div className="input-field">
            <br />
            <div className="full-text-toggle custom_check-box">
              <div className="check-box">
                <>
                  <input
                    type="checkbox"
                    id="header"
                    checked={emailData.autoEmail} // controlled value
                    onChange={(e) => {
                      setEmailData((prev) => ({
                        ...prev,
                        autoEmail: e.target.checked,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        autoEmail: "",
                      }));
                    }}
                  />
                  <label htmlFor="header">{t("auto_email_txt")}</label>{" "}
                  &nbsp;&nbsp;&nbsp;
                </>
              </div>
            </div>
          </div> */}
        </div>
      </div>
      <div className="fltr-sub-val chart-Hdr">
        {topOrderedCustomers.length > 0 && (
          <span className="fltr-val">
            {t("total_record_txt")}
            <strong>{topOrderedCustomers.length}</strong> ({getFilterLabel()})
            {selectedAccountsInternal.length > 0 && (
              <>
                {" "}
                <span>&nbsp;|&nbsp;</span> {t("total_selected_records_txt")}:{" "}
                <strong>{selectedAccountsInternal.length}</strong>
              </>
            )}
          </span>
        )}
      </div>
      <div
        className={`table_overflow checkout-table logged-in-notOrd-table ${loadingbuffer}`}
      >
        <DataTable
          columns={columns}
          data={topOrderedCustomers}
          customStyles={customStyles}
          noDataComponent={
            <div className="no-records-found">{t("no_record_txt")}</div>
          }
          pagination
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25, 50, 75, 100]}
          paginationComponentOptions={paginationComponentOptions}
        />
      </div>
      {showQueuedPopup && (
        <Popup
          extClass="order-pop customer-list-pop"
          closebtn={
            <>
              <div className="pop-header-top order-top">
                <div className="head">
                  <h4>
                  
                    {selectedAccountCode && ` ${selectedAccountCode} - `}
                    {t("queued_email_details")}
                  </h4>
                </div>
                <span
                  onClick={() => setShowQueuedPopup(false)}
                  className="close"
                ></span>
              </div>
            </>
          }
          bodyMoreTxt={
            <>
              <div className="modal-table">
                {queuedLoading ? (
                  <div className="loading-overlay">
                    <div className="loading-message">{t("loading_txt")}</div>
                  </div>
                ) : (
                  <div className="checkout-table">
                    <div className="table_overflow">
                      {queuedPopupData?.length > 0 ? (
                        <DataTable
                          columns={queuedColumns}
                          data={queuedPopupData}
                          pagination
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="no-records-found">
                              {t("no_record_txt")}
                            </div>
                          }
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25, 50, 75, 100]}
                          paginationComponentOptions={
                            paginationComponentOptions
                          }
                        />
                      ) : (
                        <div className="no-records-found">
                          {t("no_record_txt")}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          }
        />
      )}
    </div>
  );
};
